import { request } from './client';

// Types
export interface ReferralCode {
  id: string;
  userId: string;
  code: string;
  regeneratedAt: string | null;
  regenerationCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface Referral {
  id: string;
  referrerCodeId: string;
  referrerId: string;
  referredUserId: string;
  createdAt: string;
  referrer?: {
    id: string;
    email: string;
    displayName: string;
  };
  referredUser?: {
    id: string;
    email: string;
    displayName: string;
  };
}

export interface ReferralStats {
  totalReferrals: number;
  creditsEarned: number;
  nextTierAt: number | null;
  nextTierReward: number | null;
  nextTierName: string | null;
}

export interface ReferralRewardTier {
  id: string;
  minReferrals: number;
  creditsAwarded: number;
  name: string;
  description: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ReferralRewardLog {
  id: string;
  userId: string;
  tierId: string;
  referralCount: number;
  creditsAwarded: number;
  createdAt: string;
  tier?: ReferralRewardTier;
}

export interface CodeValidationResult {
  valid: boolean;
  code?: string;
  error?: string;
}

export interface RegenerateCodeResult extends ReferralCode {
  error?: string;
  daysRemaining?: number;
}

// Admin types
export interface ReferralsListResponse {
  referrals: Referral[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateTierInput {
  minReferrals: number;
  creditsAwarded: number;
  name: string;
  description?: string;
}

export interface UpdateTierInput {
  minReferrals?: number;
  creditsAwarded?: number;
  name?: string;
  description?: string;
  active?: boolean;
}

// API functions
export const referralApi = {
  // Public endpoints
  validateCode: (code: string): Promise<CodeValidationResult> =>
    request(`/api/squareup/referral/validate?code=${encodeURIComponent(code)}`),

  getTiers: (): Promise<ReferralRewardTier[]> =>
    request('/api/squareup/referral/tiers'),

  // Protected endpoints
  getMyCode: (): Promise<ReferralCode> =>
    request('/api/squareup/referral/my-code'),

  regenerateCode: (): Promise<RegenerateCodeResult> =>
    request('/api/squareup/referral/regenerate', { method: 'POST' }),

  getStats: (): Promise<ReferralStats> =>
    request('/api/squareup/referral/stats'),

  getReferrals: (): Promise<Referral[]> =>
    request('/api/squareup/referral/referrals'),

  getRewards: (): Promise<ReferralRewardLog[]> =>
    request('/api/squareup/referral/rewards'),
};

// Admin API functions
export const adminReferralApi = {
  listReferrals: (params?: {
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }): Promise<ReferralsListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.dateFrom) queryParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.set('dateTo', params.dateTo);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    const query = queryParams.toString();
    return request(`/api/squareup/admin/referrals${query ? `?${query}` : ''}`);
  },

  listTiers: (includeInactive = false): Promise<ReferralRewardTier[]> =>
    request(`/api/squareup/admin/referral-tiers${includeInactive ? '?includeInactive=true' : ''}`),

  createTier: (input: CreateTierInput): Promise<ReferralRewardTier> =>
    request('/api/squareup/admin/referral-tiers', { method: 'POST', body: input }),

  updateTier: (id: string, input: UpdateTierInput): Promise<ReferralRewardTier> =>
    request(`/api/squareup/admin/referral-tiers/${id}`, { method: 'PATCH', body: input }),

  deleteTier: (id: string): Promise<void> =>
    request(`/api/squareup/admin/referral-tiers/${id}`, { method: 'DELETE' }),
};
